import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Mail, Phone, Inbox, Users, RefreshCw, ExternalLink, Plus, Pencil, Trash2, Eye, EyeOff, Link2, Copy } from "lucide-react";
import { toast } from "sonner";
import { fetchMe, fetchContacts, fetchSubscribers, fetchStats, fetchAdminPosts, createPost, updatePost, deletePost, fetchNewsletterHtml, clearToken, getToken } from "@/lib/adminApi";
import { SITE } from "@/data/site";

const EMPTY_POST = { title: "", summary: "", cover: "", body: "", tags: "", published: false };

const field = "w-full bg-brand-bg border border-brand-border rounded-xl px-4 py-3 text-brand-navy placeholder:text-brand-muted/60 focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition";

export default function Admin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("enquiries");
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ contacts: 0, subscribers: 0 });
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, title,...}
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [c, s, st, p] = await Promise.all([fetchContacts(), fetchSubscribers(), fetchStats(), fetchAdminPosts()]);
      setContacts(c);
      setSubs(s);
      setStats(st);
      setPosts(p);
    } catch (err) {
      toast.error("Session expired. Please sign in again.");
      clearToken();
      navigate("/admin/login");
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        await fetchMe();
        await loadAll();
      } catch {
        clearToken();
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const logout = () => {
    clearToken();
    toast.success("Signed out");
    navigate("/admin/login");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  // ---------- Blog handlers ----------
  const openNew = () => setEditing({ ...EMPTY_POST });
  const openEdit = (p) => setEditing({ ...p, tags: (p.tags || []).join(", ") });
  const cancelEdit = () => setEditing(null);

  const savePost = async () => {
    if (!editing.title.trim() || !editing.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        summary: editing.summary,
        cover: editing.cover,
        body: editing.body,
        tags: (editing.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
        published: editing.published,
      };
      if (editing.id) {
        await updatePost(editing.id, payload);
        toast.success("Post updated");
      } else {
        await createPost(payload);
        toast.success("Post created");
      }
      setEditing(null);
      await loadAll();
    } catch (err) {
      toast.error("Could not save post.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await updatePost(p.id, { published: !p.published });
      await loadAll();
    } catch {
      toast.error("Could not update publish state.");
    }
  };

  const removePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await deletePost(p.id);
      toast.success("Post deleted");
      await loadAll();
    } catch {
      toast.error("Could not delete post.");
    }
  };

  const copyShareLink = async (p) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/blog/${p.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — paste into WhatsApp or LinkedIn for a rich preview.");
    } catch {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  const copyNewsletterHtml = async (p) => {
    try {
      const html = await fetchNewsletterHtml(p.id);
      await navigator.clipboard.writeText(html);
      toast.success("Newsletter HTML copied — paste into Gmail (compose → paste) or Mailchimp.");
    } catch {
      toast.error("Could not copy newsletter HTML.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-muted">
        <RefreshCw className="h-6 w-6 animate-spin mr-3" /> Loading inbox…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      <header className="bg-brand-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={SITE.logo} alt="" className="h-10 w-10 rounded-md bg-white p-1 object-contain" />
            <div>
              <p className="font-serif text-lg leading-none">Arivu Finserve</p>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent mt-1">Enquiry Inbox</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadAll} data-testid="admin-refresh" className="p-2.5 rounded-full hover:bg-white/10 transition-colors" title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-sm px-4 py-2 transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-brand-border rounded-2xl p-6">
            <div className="flex items-center gap-2 text-brand-muted text-sm"><Inbox className="h-4 w-4 text-brand-green" /> Enquiries</div>
            <div className="font-serif text-4xl text-brand-navy mt-2" data-testid="stat-contacts">{stats.contacts}</div>
          </div>
          <div className="bg-white border border-brand-border rounded-2xl p-6">
            <div className="flex items-center gap-2 text-brand-muted text-sm"><Users className="h-4 w-4 text-brand-green" /> Subscribers</div>
            <div className="font-serif text-4xl text-brand-navy mt-2" data-testid="stat-subscribers">{stats.subscribers}</div>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          {[
            { id: "enquiries", label: `Enquiries (${contacts.length})` },
            { id: "subscribers", label: `Subscribers (${subs.length})` },
            { id: "blog", label: `Blog (${posts.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id ? "bg-brand-navy text-white" : "bg-white border border-brand-border text-brand-muted hover:text-brand-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="space-y-4" data-testid="enquiries-list">
            {contacts.length === 0 && <p className="text-brand-muted">No enquiries yet.</p>}
            {contacts.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-white border border-brand-border rounded-2xl p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-xl text-brand-navy">{c.name}</p>
                    {c.service && <span className="inline-block mt-1 text-xs bg-brand-green/10 text-brand-green rounded-full px-3 py-1">{c.service}</span>}
                  </div>
                  <span className="text-brand-muted text-sm">{fmt(c.created_at)}</span>
                </div>
                <p className="text-brand-navy/80 font-light mt-4 leading-relaxed">{c.message}</p>
                <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-brand-border text-sm">
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-green transition-colors"><Mail className="h-4 w-4" /> {c.email}</a>
                  {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-green transition-colors"><Phone className="h-4 w-4" /> {c.phone}</a>}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {tab === "subscribers" && (
          <div className="bg-white border border-brand-border rounded-2xl divide-y divide-brand-border" data-testid="subscribers-list">
            {subs.length === 0 && <p className="text-brand-muted p-6">No subscribers yet.</p>}
            {subs.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-6 py-4">
                <a href={`mailto:${s.email}`} className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-green transition-colors">
                  <Mail className="h-4 w-4" /> {s.email} <ExternalLink className="h-3 w-3 opacity-50" />
                </a>
                <span className="text-brand-muted text-sm">{fmt(s.created_at)}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "blog" && (
          <div data-testid="blog-admin">
            {editing ? (
              <div className="bg-white border border-brand-border rounded-2xl p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-2xl text-brand-navy">{editing.id ? "Edit post" : "New post"}</h2>
                  <button onClick={cancelEdit} className="text-brand-muted hover:text-brand-navy text-sm">Cancel</button>
                </div>
                <div className="grid gap-4">
                  <input className={field} data-testid="post-title" placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                  <input className={field} data-testid="post-summary" placeholder="Summary (short description shown on cards & social)" value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} />
                  <input className={field} data-testid="post-cover" placeholder="Cover image URL (optional)" value={editing.cover} onChange={(e) => setEditing({ ...editing, cover: e.target.value })} />
                  <input className={field} data-testid="post-tags" placeholder="Tags (comma separated, e.g. Retirement, Tax)" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
                  <div>
                    <textarea className={`${field} min-h-[240px] resize-y font-mono text-sm`} data-testid="post-body" placeholder={"Body.\n\nFormatting:\n## Heading\n- Bullet point\nhttps://…image.jpg (image on its own line)\nBlank line = new paragraph"} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
                    <p className="text-brand-muted text-xs mt-2">Formatting: <code>## </code>Heading · <code>- </code>bullet · paste an image URL on its own line · blank line for a new paragraph.</p>
                  </div>
                  <label className="flex items-center gap-3 text-brand-navy text-sm">
                    <input type="checkbox" data-testid="post-published" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-brand-green" />
                    Publish immediately (visible on the site)
                  </label>
                  <div className="flex gap-3">
                    <button onClick={savePost} data-testid="post-save" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white text-sm font-medium px-7 py-3 hover:bg-brand-green transition-colors disabled:opacity-60">
                      {saving ? "Saving…" : editing.id ? "Save changes" : "Create post"}
                    </button>
                    <button onClick={cancelEdit} className="rounded-full border border-brand-border text-brand-navy text-sm px-7 py-3 hover:bg-brand-bg transition-colors">Cancel</button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-serif text-2xl text-brand-navy">Posts</h2>
                  <button onClick={openNew} data-testid="post-new" className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white text-sm font-medium px-5 py-2.5 hover:bg-brand-green transition-colors">
                    <Plus className="h-4 w-4" /> New post
                  </button>
                </div>
                {posts.length === 0 && <p className="text-brand-muted">No posts yet. Create your first one.</p>}
                <div className="space-y-3" data-testid="posts-list">
                  {posts.map((p) => (
                    <div key={p.id} className="bg-white border border-brand-border rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        {p.cover && <img src={p.cover} alt="" className="h-14 w-20 rounded-lg object-cover hidden sm:block" />}
                        <div className="min-w-0">
                          <p className="font-serif text-lg text-brand-navy truncate">{p.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs rounded-full px-2.5 py-0.5 ${p.published ? "bg-brand-green/10 text-brand-green" : "bg-slate-100 text-brand-muted"}`}>
                              {p.published ? "Published" : "Draft"}
                            </span>
                            <span className="text-brand-muted text-xs">{fmt(p.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => copyShareLink(p)} data-testid={`post-share-${p.id}`} className="p-2.5 rounded-full hover:bg-brand-bg text-brand-muted hover:text-brand-navy transition-colors" title="Copy share link (rich WhatsApp/LinkedIn preview)">
                          <Link2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => copyNewsletterHtml(p)} data-testid={`post-newsletter-${p.id}`} className="p-2.5 rounded-full hover:bg-brand-bg text-brand-muted hover:text-brand-navy transition-colors" title="Copy newsletter HTML — paste into Gmail/Mailchimp">
                          <Copy className="h-4 w-4" />
                        </button>
                        <button onClick={() => togglePublish(p)} data-testid={`post-toggle-${p.id}`} className="p-2.5 rounded-full hover:bg-brand-bg text-brand-muted hover:text-brand-navy transition-colors" title={p.published ? "Unpublish" : "Publish"}>
                          {p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button onClick={() => openEdit(p)} data-testid={`post-edit-${p.id}`} className="p-2.5 rounded-full hover:bg-brand-bg text-brand-muted hover:text-brand-navy transition-colors" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => removePost(p)} data-testid={`post-delete-${p.id}`} className="p-2.5 rounded-full hover:bg-red-50 text-brand-muted hover:text-red-600 transition-colors" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
